import { ReorderableList } from "@/src/components/mobile/ReoardrableListV2/ReordrableList";
import { Positions } from "@/src/components/Positions";
import {
  AHSeparator,
  AHSwitch,
  AHText,
  Box,
  Dialog,
  ListItem,
  useLayoutContext,
} from "arkh";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { graphql } from "relay-runtime";
import { RoutineStoreCustomizeSectionsQuery } from "./__generated__/RoutineStoreCustomizeSectionsQuery.graphql";
import { RoutineStoreCustomizeType } from "./RoutineStoreCustomize";
import {
  ReordrableListItem,
  ReordrableListItemProps,
} from "@/src/components/mobile/ReoardrableListV2/ReordrableListItem";
import { RoutineStoreCustomizeSections_routineSection$key } from "./__generated__/RoutineStoreCustomizeSections_routineSection.graphql";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { RoutineOccasionScreenRoutineDescription } from "../RoutineOccasion/RoutineOccasionScreen/RoutineOccasionScreenRoutineDescription";
import { RoutineStoreCustomizeSectionsSaveMutation } from "./__generated__/RoutineStoreCustomizeSectionsSaveMutation.graphql";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { Keyboard, Pressable, ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useRoutineStoreCustomizeState } from "./State";
import { RoutineStoreCustomizeSections_dialogRoles$key } from "./__generated__/RoutineStoreCustomizeSections_dialogRoles.graphql";
import { RoleListItem } from "widgets/src/RoleListItem/RoleListItem";
import { ToolbarButtonAssignees } from "widgets/src/Todo/TodoToolbar/ToolbarButtonAssignees";
import { FlashList } from "@shopify/flash-list";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { useEvent } from "widgets/src/useEvent";

export function RoutineStoreCustomizeSections(
  props: RoutineStoreCustomizeType<"RoutineStoreCustomizeSections">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => <InnerRoutineStoreCustomizeSections {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerRoutineStoreCustomizeSections(
  props: RoutineStoreCustomizeType<"RoutineStoreCustomizeSections"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<RoutineStoreCustomizeSectionsQuery>(
    graphql`
      query RoutineStoreCustomizeSectionsQuery($routineId: ID!, $storeId: ID) {
        routine(id: $routineId) {
          id
          ...RoutineOccasionScreenRoutineDescription_Routine
          assignedRoles {
            role {
              id
            }
          }
          assignableRoles(storeId: $storeId) {
            id
            name
            ...RoutineStoreCustomizeSections_dialogRoles
            ...ToolbarButtonAssignees_roles
          }
          activeTaskList(storeId: $storeId) {
            sections {
              id
              isDisabled
              canBeDisabled

              tasks {
                canBeDisabled
                isDisabled
                taskId
              }
              ...RoutineStoreCustomizeSections_routineSection
            }
          }
        }
      }
    `,
    {
      routineId: props.route.params.routineId,
      storeId: props.route.params.storeId,
    },
    { fetchPolicy: "store-and-network", fetchKey: props.fetchKey }
  );
  const items = useMemo(
    () => data.routine?.activeTaskList?.sections ?? [],
    [data.routine?.activeTaskList?.sections]
  );
  const LIST_ITEM_HEIGHT = 70;
  type Section = (typeof items)[number];

  const keyExtractor = (item: Section) => item.id;

  const { state, setState } = useRoutineStoreCustomizeState();

  const sectionOrder = useMemo(() => {
    return (
      state.sectionOrder ??
      Object.fromEntries(items.map((item, i) => [item.id, i]))
    );
  }, [state.sectionOrder, items]);

  const initialDisabledSections = useMemo(() => {
    return items.filter((item) => item.isDisabled).map((item) => item.id);
  }, [items]);

  const disabledSections = useMemo(() => {
    return state.disabledSections ?? initialDisabledSections;
  }, [state.disabledSections, initialDisabledSections]);

  const availableRoles = data.routine?.assignableRoles ?? [];

  const initialAssignedRoleIds = useMemo(() => {
    return (
      data.routine?.assignedRoles?.flatMap((ar) =>
        ar.role?.id ? [ar.role.id] : []
      ) ?? []
    );
  }, [data.routine?.assignedRoles]);

  const assignedRoleIds = state.assignedRoleIds ?? initialAssignedRoleIds;

  const selectedRoles = useMemo(() => {
    return availableRoles.filter((role) => assignedRoleIds.includes(role.id));
  }, [availableRoles, assignedRoleIds]);

  const [dialogVisible, setDialogVisible] = useState(false);

  const isDirty =
    state.sectionOrder ||
    state.disabledSections ||
    state.disabledTasks ||
    state.taskOrder ||
    state.assignedRoleIds;

  const [save, loadingSave] =
    useMutation<RoutineStoreCustomizeSectionsSaveMutation>(graphql`
      mutation RoutineStoreCustomizeSectionsSaveMutation(
        $routineId: ID!
        $input: OrgLocalConfigInput!
      ) {
        routine(id: $routineId) {
          setOrgLocalConfig(input: $input) {
            ...RoutineOccasionScreenRoutineDescription_Routine

            assignedRoles {
              role {
                id
              }
            }
            activeTaskList {
              sections {
                id
                isDisabled
                tasks {
                  id
                }
                ...RoutineStoreCustomizeSections_routineSection
              }
            }
          }
        }
      }
    `);

  const sortedSections = useMemo(() => {
    return items
      .slice()
      .sort((a, b) => sectionOrder[a.id] - sectionOrder[b.id]);
  }, [items, sectionOrder]);

  function onSave() {
    const sections = sortedSections.map((section) => {
      return {
        sectionId: section.id,
        disabled: disabledSections.includes(section.id),
        taskIds: state.taskOrder?.[section.id]
          ? Object.entries(state.taskOrder[section.id])
              .sort((a, b) => a[1] - b[1])
              .map(([taskId, _]) => taskId)
          : section.tasks.map((t) => t.taskId),
        disabledTaskIds: state.disabledTasks?.[section.id]
          ? state.disabledTasks[section.id]
          : section.tasks.flatMap((t) => (t.isDisabled ? [t.taskId] : [])),
      };
    });

    save({
      variables: {
        routineId: props.route.params.routineId,

        input: {
          storeId: props.route.params.storeId ?? null,
          sections,
          assignedRoleIds: state.assignedRoleIds ?? undefined,
        },
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
      onError: (error) => {
        console.log(error);
      },
    });
  }

  const onReorder = (order: Positions) => {
    setState((p) => ({
      ...p,
      sectionOrder: order,
    }));
  };

  useNavigationHeader({
    type: "SAVE",
    onSave: onSave,
    saveLoading: loadingSave,
    disabled: !isDirty,
    onCancel: () => {
      props.navigation.goBack();
    },
    defaultContentStyle: true,
  });

  const safeAreaInsets = useSafeAreaInsets();
  const layout = useLayoutContext();
  if (!data.routine) {
    return null;
  }
  return (
    <ScrollView
      contentContainerStyle={{
        paddingBottom: safeAreaInsets.bottom + layout.space.large,
      }}
    >
      <RoutineOccasionScreenRoutineDescription
        routine={data.routine}
        hasRoutineAdmin={false}
      >
        {availableRoles.length > 0 ? (
          <Box
            borderColor="border-primary"
            style={{ borderWidth: 1 }}
            color="surface"
            cornerRadius="large"
          >
            <Box
              style={{ flexDirection: "row", flexWrap: "wrap" }}
              gap="small"
              space="medium"
            >
              <ToolbarButtonAssignees
                assignedEmployees={[]}
                assignedRoles={selectedRoles}
                isLocked={false}
                onPress={() => {
                  Keyboard.dismiss();
                  setDialogVisible(true);
                }}
              />
            </Box>
          </Box>
        ) : null}
      </RoutineOccasionScreenRoutineDescription>
      {availableRoles.length > 0 ? (
        <AssignedRolesDialog
          visible={dialogVisible}
          onClose={() => setDialogVisible(false)}
          roles={availableRoles}
          assignedRoleIds={assignedRoleIds}
          setAssignedRoleIds={(roleIds) => {
            setState((p) => ({ ...p, assignedRoleIds: roleIds }));
          }}
        />
      ) : null}
      <Box spaceHorizontal="medium">
        <ReorderableList
          items={sortedSections}
          itemHeight={LIST_ITEM_HEIGHT}
          renderItem={(item, r) => (
            <SectionListItem
              key={item.id}
              section={item}
              reorderableListItemProps={r}
              disabled={disabledSections.includes(item.id)}
              onPress={() => {
                props.navigation.navigate(
                  "RoutineStoreCustomizeSectionsSection",
                  {
                    routineId: props.route.params.routineId,
                    storeId: props.route.params.storeId,
                    sectionId: item.id,
                  }
                );
              }}
              toggleDisabled={(id) => {
                setState((p) => {
                  const currentDisabled =
                    p.disabledSections ?? initialDisabledSections;
                  const disabledSections = currentDisabled.includes(id)
                    ? currentDisabled.filter((_id) => _id !== id)
                    : [...currentDisabled, id];
                  return {
                    ...p,
                    disabledSections,
                  };
                });
              }}
            />
          )}
          keyExtractor={keyExtractor}
          onReorder={onReorder}
        />
      </Box>
    </ScrollView>
  );
}

function AssignedRolesDialog(props: {
  visible: boolean;
  onClose: () => void;
  roles: RoutineStoreCustomizeSections_dialogRoles$key;
  assignedRoleIds: string[];
  setAssignedRoleIds: (roleIds: string[]) => void;
}) {
  const intl = useIntl();

  const roles = useFragment(
    graphql`
      fragment RoutineStoreCustomizeSections_dialogRoles on EmployeeRole
      @relay(plural: true) {
        id
        name
        ...RoleListItem_role
      }
    `,
    props.roles
  );

  const [initialAssignedRoleIds, setInitialAssignedRoleIds] = useState(
    props.assignedRoleIds
  );

  useEffect(() => {
    if (props.visible) {
      setInitialAssignedRoleIds(props.assignedRoleIds);
    }
  }, [props.visible]);

  const sortedRoles = [...roles].sort((a, b) => {
    const aSelected = initialAssignedRoleIds.includes(a.id) ? 1 : 0;
    const bSelected = initialAssignedRoleIds.includes(b.id) ? 1 : 0;
    return bSelected - aSelected;
  });

  const onPressRole = useEvent((id: string) => {
    onPressInHaptic();
    const { assignedRoleIds, setAssignedRoleIds } = props;
    setAssignedRoleIds(
      assignedRoleIds.includes(id)
        ? assignedRoleIds.filter((existingId) => existingId !== id)
        : [...assignedRoleIds, id]
    );
  });

  const renderItem = ({ item }: { item: (typeof sortedRoles)[number] }) => {
    const selected = props.assignedRoleIds.includes(item.id);
    return (
      <RoleListItem role={item} selected={selected} onPress={onPressRole} />
    );
  };

  const title = intl.formatMessage({
    defaultMessage: "Assigned roles",
    description: "RoutineStoreCustomizeSections assigned roles dialog title",
  });

  return (
    <Dialog
      visible={props.visible}
      onClose={props.onClose}
      height={"60%"}
      maxHeight={400}
      title={title}
    >
      <AHSeparator />
      <Box style={{ flex: 1 }}>
        <FlashList
          data={sortedRoles}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={AHSeparator}
        />
      </Box>
    </Dialog>
  );
}

function SectionListItem(
  props: ReordrableListItemProps & {
    section: RoutineStoreCustomizeSections_routineSection$key;
    disabled: boolean;
    toggleDisabled: (id: string) => void;
  }
) {
  const section = useFragment(
    graphql`
      fragment RoutineStoreCustomizeSections_routineSection on RoutineTaskListSection {
        tasks {
          __typename
        }
        title
        id
        canBeDisabled
      }
    `,
    props.section
  );
  const title = useMemo(() => section.title, [section.title]);

  const intl = useIntl();
  const placeholder = intl.formatMessage({
    defaultMessage: "Section name",
    description: "SectionSelectItem section name placeholder",
  });
  const hasPlaceholder = title === "";

  return (
    <ReordrableListItem
      {...props}
      disabled={false}
      border="border-primary"
      onPress={() => {
        props.onPress?.();
      }}
      reorderableListItemProps={{
        ...props.reorderableListItemProps,
      }}
      right={
        <Box
          gap="medium"
          style={{ flexDirection: "row", alignItems: "center" }}
        >
          {section.tasks.length > 0 ? (
            <AHText>
              <FormattedMessage
                defaultMessage="{count} tasks"
                description="SectionListItem tasks count"
                values={{ count: section.tasks.length }}
              />
            </AHText>
          ) : null}
          {section.canBeDisabled ? (
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
              }}
            >
              <AHSwitch
                value={!props.disabled}
                onValueChange={() => {
                  props.toggleDisabled(section.id);
                }}
              />
            </Pressable>
          ) : null}
          {section.tasks.length > 0 ? <ListItem.NavArrow /> : null}
        </Box>
      }
    >
      <ListItem.Title color={hasPlaceholder ? "text-washed" : undefined}>
        {hasPlaceholder ? placeholder : title}
      </ListItem.Title>
    </ReordrableListItem>
  );
}
